import { Component, OnInit, signal, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RoleService } from '../../core/services/role.service';
import { ToastService } from '../../core/services/toast.service';
import { AuthService } from '../../core/services/auth.service';
import { RoleDto, PermissionDto } from '../../core/models/models';
import { ModalComponent } from '../../shared/components/modal/modal.component';

@Component({
  selector: 'app-role-management',
  standalone: true,
  imports: [CommonModule, FormsModule, ModalComponent],
  template: `
    <div class="space-y-6 font-sans">
      <!-- Header Section -->
      <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
        <div>
          <h1 class="text-xl font-extrabold text-slate-900 flex items-center gap-2">
            <svg class="w-6 h-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
            Role-Based Access Control (RBAC) Matrix
          </h1>
          <p class="text-xs text-slate-500 mt-1">
            Configure system roles, fine-grained action permissions, and assign security capabilities dynamically across e-Gov entities.
          </p>
        </div>

        @if (authService.hasRole('SuperAdmin') || authService.isInternal()) {
          <button
            (click)="openCreateModal()"
            class="px-4 py-2.5 rounded-xl text-xs font-bold bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white shadow-md shadow-emerald-600/20 flex items-center gap-2 transition cursor-pointer active:scale-95"
          >
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
            </svg>
            Define Custom Role
          </button>
        }
      </div>

      <!-- Quick Capability Summary Pill -->
      <div class="p-3.5 rounded-2xl bg-emerald-50/70 border border-emerald-200/80 flex flex-wrap items-center justify-between gap-3 text-xs text-emerald-900">
        <div class="flex items-center gap-2.5">
          <span class="p-1.5 rounded-xl bg-emerald-200/60 text-emerald-800">
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </span>
          <span>
            <strong>Dynamic Permission Binding:</strong> Permissions assigned to roles are dynamically loaded   and govern fine-grained action authorization across the portal.
          </span>
        </div>
        <div class="text-[11px] font-bold text-emerald-800">
          Available Platform Capabilities: <span class="bg-emerald-200 px-2 py-0.5 rounded-full font-extrabold">{{ allPermissions().length }}</span>
        </div>
      </div>

      <!-- Roles Cards Grid -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
        @for (role of roles(); track role.id) {
          <div class="bg-white rounded-2xl border border-slate-200 p-5 flex flex-col justify-between shadow-xs hover:border-emerald-300 hover:shadow-md transition-all group">
            <div class="space-y-3">
              <div class="flex items-center justify-between">
                <span class="font-mono text-[11px] font-extrabold text-emerald-800 bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 rounded">
                  {{ role.code }}
                </span>
                <span class="text-[11px] font-bold text-slate-600 bg-slate-100 px-2.5 py-0.5 rounded-full border border-slate-200">
                  {{ role.userCount }} {{ role.userCount === 1 ? 'User' : 'Users' }}
                </span>
              </div>

              <div>
                <h3 class="text-base font-bold text-slate-900 group-hover:text-emerald-800 transition-colors">{{ role.name }}</h3>
                <p class="text-xs text-slate-600 mt-1 leading-relaxed line-clamp-2">{{ role.description }}</p>
              </div>

              <div class="space-y-2 pt-3 border-t border-slate-100">
                <div class="flex items-center justify-between">
                  <span class="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                    Granted Permissions ({{ role.permissions.length }})
                  </span>
                </div>

                <div class="flex flex-wrap gap-1 max-h-32 overflow-y-auto pr-1">
                  @for (perm of role.permissions; track perm) {
                    <span class="text-[10px] px-2 py-0.5 rounded bg-emerald-50 text-emerald-800 border border-emerald-200 font-semibold">
                      {{ perm }}
                    </span>
                  } @empty {
                    <span class="text-[11px] text-slate-400 italic">No permissions assigned yet</span>
                  }
                </div>
              </div>
            </div>

            <!-- Role Card Actions -->
            <div class="pt-4 mt-4 border-t border-slate-100 flex items-center justify-between">
              <span class="text-[10px] text-slate-400 font-medium">DB Persisted</span>

              @if (authService.hasRole('SuperAdmin') || authService.isInternal()) {
                <button
                  (click)="openPermissionsModal(role)"
                  class="px-3 py-1.5 rounded-xl bg-emerald-50 hover:bg-emerald-600 hover:text-white text-emerald-700 text-xs font-bold transition border border-emerald-200 flex items-center gap-1.5 cursor-pointer shadow-2xs"
                >
                  <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                  </svg>
                  <span>Configure Permissions</span>
                </button>
              }
            </div>
          </div>
        } @empty {
          <div class="col-span-full p-8 text-center bg-white rounded-2xl border border-slate-200 text-slate-500 text-xs">
            No RBAC roles currently found in system database.
          </div>
        }
      </div>

      <!-- Configure Role Permissions Modal -->
      <app-modal
        [isOpen]="showPermissionsModal()"
        [title]="'Configure Permissions: ' + (selectedRole()?.name || '')"
        (close)="showPermissionsModal.set(false)"
      >
        <div class="space-y-4 text-xs">
          <!-- Role Details Header -->
          <div class="p-3.5 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-between gap-3">
            <div>
              <div class="font-mono text-xs font-bold text-slate-900">{{ selectedRole()?.code }}</div>
              <div class="text-[11px] text-slate-500 mt-0.5">{{ selectedRole()?.description }}</div>
            </div>
            <div class="text-right shrink-0">
              <span class="text-[11px] font-bold px-2 py-0.5 rounded bg-emerald-100 text-emerald-800">
                {{ selectedPermissions().length }} Selected
              </span>
            </div>
          </div>

          <!-- Quick Bulk Actions -->
          <div class="flex items-center justify-between pt-1">
            <span class="text-slate-600 font-medium">Select capability permissions to grant to this role:</span>
            <div class="flex items-center gap-2">
              <button
                type="button"
                (click)="selectAllPermissions()"
                class="text-[11px] font-bold text-emerald-700 hover:text-emerald-900 hover:underline cursor-pointer"
              >
                Select All
              </button>
              <span class="text-slate-300">|</span>
              <button
                type="button"
                (click)="clearAllPermissions()"
                class="text-[11px] font-bold text-slate-500 hover:text-slate-800 hover:underline cursor-pointer"
              >
                Clear All
              </button>
            </div>
          </div>

          <!-- Categorized Permissions Matrix -->
          <div class="space-y-4 max-h-80 overflow-y-auto pr-1">
            @for (cat of permissionCategories(); track cat.category) {
              <div class="space-y-2">
                <div class="font-bold text-slate-800 text-[11px] uppercase tracking-wider pb-1 border-b border-slate-200 flex items-center gap-1.5">
                  <span class="w-1.5 h-1.5 rounded-full bg-emerald-600"></span>
                  {{ cat.category }}
                </div>

                <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  @for (perm of cat.perms; track perm.id) {
                    <label
                      class="flex items-start gap-2.5 p-2.5 rounded-xl border transition cursor-pointer"
                      [ngClass]="isPermissionSelected(perm.name) ? 'bg-emerald-50/70 border-emerald-300 text-emerald-950' : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300'"
                    >
                      <input
                        type="checkbox"
                        [checked]="isPermissionSelected(perm.name)"
                        (change)="togglePermission(perm.name)"
                        class="w-4 h-4 rounded text-emerald-600 border-slate-300 focus:ring-emerald-500 mt-0.5"
                      />
                      <div class="flex-1 min-w-0">
                        <div class="font-bold text-xs">{{ perm.name }}</div>
                        <div class="text-[10px] text-slate-500 leading-tight mt-0.5">{{ perm.description }}</div>
                      </div>
                    </label>
                  }
                </div>
              </div>
            }
          </div>

          <!-- Modal Action Buttons -->
          <div class="flex items-center justify-end gap-3 pt-3 border-t border-slate-200">
            <button
              type="button"
              (click)="showPermissionsModal.set(false)"
              class="px-4 py-2 rounded-xl text-xs font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-300 cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="button"
              (click)="onSaveRolePermissions()"
              [disabled]="isSavingPermissions()"
              class="px-4 py-2 rounded-xl text-xs font-bold bg-emerald-600 text-white hover:bg-emerald-500 shadow-sm cursor-pointer disabled:opacity-50 flex items-center gap-1.5"
            >
              @if (isSavingPermissions()) {
                <span class="animate-spin text-sm">↻</span>
              }
              <span>{{ isSavingPermissions() ? 'Saving...' : 'Save Permissions' }}</span>
            </button>
          </div>
        </div>
      </app-modal>

      <!-- Define Custom Role Modal -->
      <app-modal
        [isOpen]="showCreateModal()"
        title="Define Custom Security Role"
        (close)="showCreateModal.set(false)"
      >
        <form (ngSubmit)="onCreateRole()" class="space-y-4 text-xs">
          <div>
            <label class="block text-slate-700 font-bold mb-1 uppercase tracking-wider text-[10px]">
              Role Name <span class="text-rose-500">*</span>
            </label>
            <input
              type="text"
              [(ngModel)]="newRoleName"
              name="roleName"
              required
              placeholder="e.g. Compliance Officer"
              class="w-full bg-white border border-slate-300 text-slate-900 rounded-xl px-3 py-2 text-xs focus:border-emerald-500 focus:outline-none"
            />
          </div>

          <div>
            <label class="block text-slate-700 font-bold mb-1 uppercase tracking-wider text-[10px]">
              Description
            </label>
            <textarea
              [(ngModel)]="newRoleDescription"
              name="roleDescription"
              rows="2"
              placeholder="Operational responsibilities, scope, and duties..."
              class="w-full bg-white border border-slate-300 text-slate-900 rounded-xl px-3 py-2 text-xs focus:border-emerald-500 focus:outline-none"
            ></textarea>
          </div>

          <!-- Initial Permissions Selection -->
          <div class="space-y-2 pt-2 border-t border-slate-100">
            <div class="flex items-center justify-between">
              <label class="block text-slate-700 font-bold uppercase tracking-wider text-[10px]">
                Assign Initial Permissions ({{ newRolePermissions().length }} Selected)
              </label>
              <div class="flex items-center gap-2">
                <button
                  type="button"
                  (click)="newRolePermissions.set(allPermissionNames())"
                  class="text-[10px] font-bold text-emerald-700 hover:underline cursor-pointer"
                >
                  All
                </button>
                <span class="text-slate-300">|</span>
                <button
                  type="button"
                  (click)="newRolePermissions.set([])"
                  class="text-[10px] font-bold text-slate-500 hover:underline cursor-pointer"
                >
                  None
                </button>
              </div>
            </div>

            <div class="space-y-3 max-h-56 overflow-y-auto pr-1">
              @for (cat of permissionCategories(); track cat.category) {
                <div class="space-y-1.5">
                  <div class="font-bold text-slate-700 text-[10px] uppercase tracking-wide">{{ cat.category }}</div>
                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                    @for (perm of cat.perms; track perm.id) {
                      <label class="flex items-center gap-2 p-2 rounded-lg border border-slate-200 hover:border-emerald-300 bg-slate-50/50 cursor-pointer">
                        <input
                          type="checkbox"
                          [checked]="newRolePermissions().includes(perm.name)"
                          (change)="toggleNewRolePermission(perm.name)"
                          class="w-3.5 h-3.5 rounded text-emerald-600 border-slate-300 focus:ring-emerald-500"
                        />
                        <span class="text-[11px] font-semibold text-slate-800 truncate">{{ perm.name }}</span>
                      </label>
                    }
                  </div>
                </div>
              }
            </div>
          </div>

          <div class="flex items-center justify-end gap-3 pt-3 border-t border-slate-200">
            <button
              type="button"
              (click)="showCreateModal.set(false)"
              class="px-4 py-2 rounded-xl text-xs font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-300 cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              [disabled]="isCreatingRole()"
              class="px-4 py-2 rounded-xl text-xs font-bold bg-emerald-600 text-white hover:bg-emerald-500 shadow-sm cursor-pointer disabled:opacity-50"
            >
              {{ isCreatingRole() ? 'Creating...' : 'Create Role' }}
            </button>
          </div>
        </form>
      </app-modal>
    </div>
  `
})
export class RoleManagementComponent implements OnInit {
  roleService = inject(RoleService);
  toastService = inject(ToastService);
  authService = inject(AuthService);

  roles = signal<RoleDto[]>([]);
  allPermissions = signal<PermissionDto[]>([]);

  showCreateModal = signal<boolean>(false);
  showPermissionsModal = signal<boolean>(false);

  selectedRole = signal<RoleDto | null>(null);
  selectedPermissions = signal<string[]>([]);
  isSavingPermissions = signal<boolean>(false);

  newRoleName = '';
  newRoleDescription = '';
  newRolePermissions = signal<string[]>([]);
  isCreatingRole = signal<boolean>(false);

  allPermissionNames = computed(() => this.allPermissions().map(p => p.name));

  permissionCategories = computed(() => {
    const map = new Map<string, PermissionDto[]>();
    for (const p of this.allPermissions()) {
      const cat = p.category || 'General Operations';
      if (!map.has(cat)) {
        map.set(cat, []);
      }
      map.get(cat)!.push(p);
    }
    return Array.from(map.entries()).map(([category, perms]) => ({ category, perms }));
  });

  ngOnInit() {
    this.loadData();
  }

  loadData() {
    this.roleService.getAllRoles().subscribe({
      next: (res) => {
        if (res.success && res.data) {
          this.roles.set(res.data);
        }
      },
      error: () => {
        this.toastService.error('Failed to retrieve system roles');
      }
    });

    this.roleService.getAllPermissions().subscribe({
      next: (res) => {
        if (res.success && res.data) {
          this.allPermissions.set(res.data);
        }
      },
      error: () => {
        this.toastService.error('Failed to retrieve system permissions');
      }
    });
  }

  openPermissionsModal(role: RoleDto) {
    this.selectedRole.set(role);
    this.selectedPermissions.set([...(role.permissions || [])]);
    this.showPermissionsModal.set(true);
  }

  togglePermission(permName: string) {
    if (this.isPermissionSelected(permName)) {
      this.selectedPermissions.update(list => list.filter(p => p !== permName));
    } else {
      this.selectedPermissions.update(list => [...list, permName]);
    }
  }

  isPermissionSelected(permName: string): boolean {
    return this.selectedPermissions().includes(permName);
  }

  selectAllPermissions() {
    this.selectedPermissions.set(this.allPermissionNames());
  }

  clearAllPermissions() {
    this.selectedPermissions.set([]);
  }

  onSaveRolePermissions() {
    const role = this.selectedRole();
    if (!role) return;

    this.isSavingPermissions.set(true);
    this.roleService.updateRolePermissions(role.id, { permissions: this.selectedPermissions() }).subscribe({
      next: (res) => {
        this.isSavingPermissions.set(false);
        if (res.success && res.data) {
          this.toastService.success(`Permissions updated successfully for ${role.name}`);
          this.roles.update(list => list.map(r => r.id === role.id ? res.data! : r));
          this.showPermissionsModal.set(false);
        }
      },
      error: (err) => {
        this.isSavingPermissions.set(false);
        this.toastService.error(err.error?.message || 'Failed to update role permissions');
      }
    });
  }

  openCreateModal() {
    this.newRoleName = '';
    this.newRoleDescription = '';
    this.newRolePermissions.set([]);
    this.showCreateModal.set(true);
  }

  toggleNewRolePermission(permName: string) {
    if (this.newRolePermissions().includes(permName)) {
      this.newRolePermissions.update(list => list.filter(p => p !== permName));
    } else {
      this.newRolePermissions.update(list => [...list, permName]);
    }
  }

  onCreateRole() {
    if (!this.newRoleName.trim()) {
      this.toastService.error('Role name is required');
      return;
    }

    this.isCreatingRole.set(true);
    this.roleService.createRole({
      name: this.newRoleName.trim(),
      description: this.newRoleDescription.trim(),
      permissions: this.newRolePermissions()
    }).subscribe({
      next: (res) => {
        this.isCreatingRole.set(false);
        if (res.success && res.data) {
          this.toastService.success(`Role '${res.data.name}' created successfully`);
          this.roles.update(list => [...list, res.data!]);
          this.showCreateModal.set(false);
        }
      },
      error: (err) => {
        this.isCreatingRole.set(false);
        this.toastService.error(err.error?.message || 'Failed to create role');
      }
    });
  }
}
